// Thin API client. The token lives in sessionStorage for the development identities only; production uses
// httpOnly session cookies issued by the identity module (no token in browser storage).
import { DEFAULT_VALUE_BASIS, VALUE_BASIS_PARAM } from '@leo/model';
import { getValueBasis } from './ui/basis-state';
export type Role = 'owner' | 'steward' | 'analyst' | 'viewer' | 'service';
export interface Principal { id: string; name: string; role: Role }

const KEY = 'leo.dev.token';
export const devIdentities = [
  { token: 'dev-owner', label: 'Owner' }, { token: 'dev-steward', label: 'Data steward' }, { token: 'dev-viewer', label: 'Viewer' },
];
export function getToken(): string { try { return sessionStorage.getItem(KEY) ?? ''; } catch { return ''; } }
export function setToken(t: string) { try { sessionStorage.setItem(KEY, t); } catch { /* storage unavailable: stay in memory */ } }

export class ApiError extends Error { constructor(public status: number, public code: string | undefined, message: string) { super(message); } }
/** What a refused request says on screen: the server decides who sees what; the page only says so, plainly. */
const DENIED: Record<string, string> = {
  FORBIDDEN: 'Not available for your role.',
  WHOLE_GROUP: 'Not available: this view covers the whole group, and your access is limited to some stores or companies.',
  OUT_OF_SCOPE: 'Not available: outside the stores or companies you have access to.',
};
export const isDenied = (e: unknown) => e instanceof ApiError && e.status === 403 && !!e.code && e.code in DENIED;

/** Requests ride on the session cookie; a development token (sessionStorage) is sent only when one has been chosen. */
export function authHeaders(): Record<string, string> { const t = getToken(); return t ? { authorization: `Bearer ${t}` } : {}; }
/** Every API read carries the value basis chosen (ui/basis.tsx) when it is not the default — so the server works out the
 *  figures on it and its cache keeps one answer per setting; screens whose figures keep their own values ignore it. A path
 *  that names a basis itself keeps it. */
export function withBasis(path: string, method = 'GET'): string {
  const b = getValueBasis(); if (b === DEFAULT_VALUE_BASIS || method.toUpperCase() !== 'GET' || !path.startsWith('/api/v1/')) return path;
  if (new RegExp(`[?&]${VALUE_BASIS_PARAM}=`).test(path)) return path;
  const [p, hash] = path.split('#') as [string, string | undefined];
  return `${p}${p.includes('?') ? '&' : '?'}${VALUE_BASIS_PARAM}=${b}${hash != null ? `#${hash}` : ''}`;
}
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  path = withBasis(path, init.method ?? 'GET');
  const r = await fetch(path, { ...init, credentials: 'same-origin', headers: { ...authHeaders(), 'content-type': 'application/json', ...(init.headers ?? {}) } });
  const body = await r.json().catch(() => ({}));
  if (r.status === 401 && !location.pathname.startsWith('/login') && !location.pathname.startsWith('/setup')) { location.assign('/login?next=' + encodeURIComponent(location.pathname + location.search)); }
  if (r.status === 403 && body.code === 'MUST_CHANGE_PASSWORD' && !location.pathname.startsWith('/password')) { location.assign('/password'); }
  // a bare permission refusal ("requires sales.view") or a scope refusal is said plainly; a refusal that explains itself keeps its words
  const plain = r.status === 403 && body.code && DENIED[body.code] && (body.code !== 'FORBIDDEN' || /^requires /.test(body.error ?? '')) ? DENIED[body.code] : null;
  if (!r.ok) throw new ApiError(r.status, body.code, plain ?? body.error ?? r.statusText);
  return body as T;
}

export const inr = (v: number | string | null | undefined, opts: { compact?: boolean } = {}): string => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v); if (!Number.isFinite(n)) return '—';
  const a = Math.abs(n);
  const sign = n < 0 ? '−' : '';   // "−₹2.0 L", never "₹-2.0 L"
  if (opts.compact !== false) {
    // ₹1–10 Cr to 2 decimals, ₹10–100 Cr to 1 (₹11.6 Cr, not ₹12 Cr — a headline is quoted), ₹100 Cr+ whole
    if (a >= 1e7) return `${sign}₹${(a / 1e7).toFixed(a >= 1e9 ? 0 : a >= 1e8 ? 1 : 2)} Cr`;
    if (a >= 1e5) return `${sign}₹${(a / 1e5).toFixed(a >= 1e6 ? 0 : 1)} L`;
  }
  return `${sign}₹${a.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};
export const int = (v: number | string | null | undefined) => (v === null || v === undefined) ? '—' : Number(v).toLocaleString('en-IN');
export const pct = (v: number | string | null | undefined, d = 0) => (v === null || v === undefined) ? '—' : `${(Number(v) * 100).toFixed(d)}%`;
export const when = (s: string | null | undefined) => s ? new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
